import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { authAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui';
import { Link } from 'react-router-dom';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isRTL, t } = useLanguage();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError(isRTL ? 'رمز إعادة التعيين غير موجود' : 'Reset token is missing');
    }
  }, [searchParams, isRTL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!password || !confirmPassword) {
      setError(isRTL ? 'يرجى إدخال كلمة المرور' : 'Please enter password');
      return;
    }

    if (password.length < 12) {
      setError(isRTL ? 'كلمة المرور يجب أن تكون 12 حرفًا على الأقل' : 'Password must be at least 12 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError(isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return;
    }

    if (!token) {
      setError(isRTL ? 'رمز إعادة التعيين غير موجود' : 'Reset token is missing');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password.trim());
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.error || 
        (isRTL ? 'فشل إعادة تعيين كلمة المرور' : 'Failed to reset password. Token may be invalid or expired.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-white to-emerald-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden" 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Patterns */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10 animate-in fade-in duration-500">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">
            {isRTL ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
          </h2>
          <p className="text-slate-600">
            {isRTL ? 'أدخل كلمة مرور جديدة' : 'Enter your new password'}
          </p>
        </div>

        {/* Main Form Card */}
        <Card className="p-8 shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          {success ? (
            // Success Message
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-900">
                {isRTL ? 'تم إعادة تعيين كلمة المرور بنجاح!' : 'Password Reset Successful!'}
              </h3>
              <p className="text-slate-600">
                {isRTL 
                  ? 'سيتم إعادة توجيهك إلى صفحة تسجيل الدخول...'
                  : 'Redirecting you to login page...'}
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-bold transition-colors"
              >
                {isRTL ? 'الانتقال الآن' : 'Go to Login'}
                {isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              </Link>
            </div>
          ) : (
            // Reset Form
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="rounded-xl bg-red-50 border-2 border-red-200 p-4 animate-in slide-in-from-top duration-300">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800 font-medium">{error}</p>
                  </div>
                </div>
              )}

              {/* New Password Field */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-bold text-slate-700">
                  {isRTL ? 'كلمة المرور الجديدة' : 'New Password'}
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                    <Lock className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full ${isRTL ? 'pl-12 pr-12' : 'pl-12 pr-12'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                    placeholder={isRTL ? '••••••••' : '••••••••'}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-4' : 'right-0 pr-4'} flex items-center text-slate-400 hover:text-slate-600 transition-colors`}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <label htmlFor="confirm-password" className="block text-sm font-bold text-slate-700">
                  {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                    <Lock className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full ${isRTL ? 'pl-12 pr-12' : 'pl-12 pr-12'} py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all bg-white text-slate-900 placeholder-slate-400`}
                    placeholder={isRTL ? '••••••••' : '••••••••'}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-4' : 'right-0 pr-4'} flex items-center text-slate-400 hover:text-slate-600 transition-colors`}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30 py-3 text-base font-bold rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading 
                  ? (isRTL ? 'جاري إعادة التعيين...' : 'Resetting...')
                  : (isRTL ? 'إعادة تعيين كلمة المرور' : 'Reset Password')}
                {!loading && (isRTL ? <ArrowLeft className="w-5 h-5 inline-block ml-2" /> : <ArrowRight className="w-5 h-5 inline-block ml-2" />)}
              </button>

              {/* Back to Login */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-sky-600 hover:text-sky-700 font-bold transition-colors"
                >
                  {isRTL ? '← العودة لتسجيل الدخول' : 'Back to Login →'}
                </Link>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
